import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import PaneCard, { PANE_SCROLLER_CLASSNAME } from '../components/entity/PaneCard';
import ImpersonatorNotice from '../components/ImpersonatorNotice';
import {
  ICON_RAIL_WIDTH_PX,
  MAIN_SIDEBAR_WIDTH_PX,
  MOBILE_TOP_BAR_HEIGHT_PX,
} from '../components/navigation/dimensions';
import MainSidebar from '../components/navigation/MainSidebar';
import SyncingNotice from '../components/SyncingNotice';
import { useOnboardingState } from '../hooks/queries/useOnboardingState';
import { useHeaderScrollProgress } from '../hooks/scroll/useHeaderScrollProgress';
import { usePaneScrollRestoration } from '../hooks/scroll/usePaneScrollRestoration';
import AssistantPane from '../panes/assistant/AssistantPane';
import { PATHS } from '../paths';
import { useSidePanel } from '../providers/SidePanelProvider';
import { SIDE_PANEL_TRANSITION_DURATION_STYLE } from '../utils/sidePanelMotion';

import type { PresenceStage } from '../hooks/transition/useTransitionedPresence';

/** The side panel's width floor. */
const SIDE_PANEL_WIDTH_PX = 460;
/** The side panel's width ceiling (Figma: "Assistant: 40% • Max-Width: 640px"). */
const SIDE_PANEL_MAX_WIDTH_PX = 640;
/** The side panel's fluid share of the row (Figma: "Assistant: 40%"). */
const SIDE_PANEL_WIDTH_FRACTION = 0.4;

/**
 * The pixel width the open column settles at — the fluid 40%-of-row value under
 * its clamps, resolved against the row's content box (the row carries the nav
 * offset as padding). Used as the explicit endpoint while the width transition
 * runs: the fluid `w-2/5` + `minWidth` pair cannot transition to zero, because
 * the 460px floor holds the *used* width while only the specified 40% animates.
 */
const measureOpenPanelWidthPx = (columnEl: HTMLElement): number => {
  const row = columnEl.parentElement;
  if (!row) {
    return SIDE_PANEL_WIDTH_PX;
  }

  const rowStyle = window.getComputedStyle(row);
  const rowContentWidth = row.clientWidth - parseFloat(rowStyle.paddingLeft) - parseFloat(rowStyle.paddingRight);
  const fluidWidth = rowContentWidth * SIDE_PANEL_WIDTH_FRACTION;
  return Math.min(Math.max(fluidWidth, SIDE_PANEL_WIDTH_PX), SIDE_PANEL_MAX_WIDTH_PX);
};

interface ISidePanelProps {
  /** Where the column is in its enter/exit transition; `open` when at rest. */
  stage: PresenceStage;
  /** Settles the column's width transition — ends the enter, unmounts after the exit. */
  onWidthTransitionEnd: React.TransitionEventHandler;
  /** The assistant header's close button — hides the column entirely. */
  onCloseAssistant: () => void;
}

/**
 * The column beside content at `xl+`, seating the assistant pane in a
 * chromeless treatment: no border, radius, or shadow, with the column's own
 * insets, fluid at 40% of the row between a 460px floor and a 640px ceiling
 * (Figma node 8052:249487; annotation "Assistant: 40% • Max-Width: 640px").
 * The pane sits directly on the app ground; the Notification Center is a layer
 * of the pane itself, so the column has nothing else to seat.
 *
 * Motion: the column's open and close animate its real width (the dashboard
 * reflows beside it, matching the design video's continuous chart re-layout)
 * while the content fades, over `SIDE_PANEL_TRANSITION_MS` with
 * `ease-in-out`, collapsing to an instant swap under `prefers-reduced-motion`.
 * While a width transition runs, the column and its content are pinned to
 * explicit pixel widths so the content clips instead of reflowing; at rest the
 * fluid clamp returns.
 */
const SidePanel = ({ stage, onWidthTransitionEnd, onCloseAssistant }: ISidePanelProps) => {
  const columnRef = React.useRef<HTMLDivElement>(null);
  const [pinnedWidthPx, setPinnedWidthPx] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const columnEl = columnRef.current;
    if (!columnEl) {
      return;
    }

    if (stage === 'preEnter') {
      setPinnedWidthPx(measureOpenPanelWidthPx(columnEl));
    } else if (stage === 'preExit') {
      setPinnedWidthPx(columnEl.offsetWidth);
    } else if (stage === 'open') {
      setPinnedWidthPx(null);
    }
  }, [stage]);

  // `preExit` before its measurement has landed still renders the at-rest
  // fluid width: the measuring layout effect must read the true open width,
  // and an interim `width: 0` frame would start the transition a frame early
  // from the wrong side.
  const isColumnAtRest = stage === 'open' || (stage === 'preExit' && pinnedWidthPx === null);
  const isColumnCollapsed = stage === 'preEnter' || stage === 'exiting';

  return (
    <div
      ref={columnRef}
      onTransitionEnd={onWidthTransitionEnd}
      style={{
        ...(isColumnAtRest
          ? { minWidth: SIDE_PANEL_WIDTH_PX, maxWidth: SIDE_PANEL_MAX_WIDTH_PX }
          : { width: isColumnCollapsed ? 0 : (pinnedWidthPx ?? 0) }),
        ...SIDE_PANEL_TRANSITION_DURATION_STYLE,
      }}
      className={cx(
        'flex h-full shrink-0 flex-col overflow-hidden',
        'transition-[width] ease-in-out motion-reduce:transition-none',
        isColumnAtRest && 'w-2/5'
      )}
    >
      <div
        style={{
          ...(pinnedWidthPx !== null ? { width: pinnedWidthPx } : undefined),
          ...SIDE_PANEL_TRANSITION_DURATION_STYLE,
        }}
        className={cx(
          'relative flex h-full min-h-0 flex-col overflow-hidden',
          pinnedWidthPx === null && 'w-full',
          'transition-opacity ease-in-out motion-reduce:transition-none',
          isColumnCollapsed ? 'opacity-0' : 'opacity-100'
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col py-4 pl-4 pr-2">
          <AssistantPane onClose={onCloseAssistant} />
        </div>
      </div>
    </div>
  );
};

const HomeScene = () => {
  const navigate = useNavigate();
  const { scrollerRef, onScroll: onHeaderScroll } = useHeaderScrollProgress();
  const { onScroll: onRestorationScroll } = usePaneScrollRestoration(scrollerRef);
  const onScroll = React.useCallback(() => {
    onHeaderScroll();
    onRestorationScroll();
  }, [onHeaderScroll, onRestorationScroll]);

  // The panel docks beside content only at `xl+`. Below that there is no room
  // for it — the nav button and the bell navigate to `/assistant` instead (see
  // MainApp's route gates). `isXl` here is the same signal that gates those
  // redirects, so the panel and the route never both mount the pane (and its
  // single WebSocket).
  const { panelPresence, toggleAssistant } = useSidePanel();
  const isXl = useBreakpoint('xl');

  // `panelPresence` (from the provider, so `useContentColumnsBreakpoint` flips
  // with the column the user sees) retains the occupant while the exit plays —
  // keeping `AssistantPane` and its WebSocket mounted until the collapse
  // finishes. The render still re-checks `isXl` even though the provider's
  // presence input folds it in: a downward crossing must unmount instantly, or
  // the exit window could double-mount the pane against the `/assistant` route.
  // Only in-`xl` toggles animate.
  const panelInColumn = isXl ? panelPresence.renderValue : null;

  const { needsOnboarding, loading: onboardingStateLoading } = useOnboardingState();

  React.useEffect(() => {
    if (!onboardingStateLoading && needsOnboarding) {
      void navigate(PATHS.ONBOARDING);
    }
  }, [onboardingStateLoading, needsOnboarding, navigate]);

  // Hold the shell until the check resolves and the about-to-redirect window
  // closes — otherwise the main app flashes in before the navigate-effect
  // above lands an onboarding-needing viewer at `/onboarding`.
  if (onboardingStateLoading || needsOnboarding) {
    return null;
  }

  return (
    <>
      <MainSidebar />
      {/*
       * Outer wrapper reserves space for the fixed `MainSidebar` via padding
       * (top bar height below `md`, icon rail at `md`, full sidebar at `lg+`).
       * `flex-1` covers the case where this scene mounts inside a flex-row
       * parent — notably the Storybook global decorator
       * (`bg-app flex h-full w-full`). Without it the wrapper would shrink to
       * its content's intrinsic width and leave a right-side gutter at wide
       * viewports. In production the scene mounts directly inside `#root`
       * (block context, no flex parent), where `flex-1` is a no-op and
       * `h-full` + block-level width handle layout — but keeping `flex-1`
       * makes the layout robust to either context. `min-w-0` lets the inner
       * content shrink below intrinsic min-width so panes can reflow at
       * narrower viewports without overflowing right.
       *
       * The content column carries the app-ground insets and seats the pane
       * card — one rounded surface holding the scroller, so the card's edges
       * stay pinned while the pane scrolls inside it and pane content fills
       * the card's width.
       *
       * At `xl+` with the side panel open this wrapper becomes a flex-row: the
       * panel docks between the nav and the content column, which holds the
       * notices track + card. The scroller's load-bearing props (`relative`,
       * `[overflow-anchor:none]`) travel with the scroller div regardless, so
       * they are unaffected.
       *
       * All three magic numbers come from `navigation/dimensions.ts` — change a
       * value there and the sidebar components, top bar, and these offsets all
       * update together.
       */}
      <div
        style={
          {
            '--nav-top-bar-h': `${MOBILE_TOP_BAR_HEIGHT_PX}px`,
            '--nav-rail-w': `${ICON_RAIL_WIDTH_PX}px`,
            '--nav-full-w': `${MAIN_SIDEBAR_WIDTH_PX}px`,
          } as React.CSSProperties
        }
        className={cx(
          'pt-(--nav-top-bar-h) md:pl-(--nav-rail-w) lg:pl-(--nav-full-w) flex h-full min-w-0 flex-1 md:pt-0',
          panelInColumn ? 'flex-row' : 'flex-col'
        )}
      >
        {panelInColumn && (
          <SidePanel
            stage={panelPresence.stage}
            onWidthTransitionEnd={panelPresence.onTransitionEnd}
            onCloseAssistant={toggleAssistant}
          />
        )}
        {/*
         * Content column: notices track + pane card. A flex child so it sits
         * beside the assistant panel when one is shown, and the sole column
         * (full width) otherwise. `min-h-0` keeps the card bounded; `min-w-0`
         * lets panes reflow when the panel narrows the available width. The
         * padding is the card's inset on the app ground; beside the open
         * assistant column (`pr-2`) the two insets sum to the designed 24px
         * seam.
         */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3 lg:p-4">
          {/*
           * Notices are app-wide banners, not pane content, so they stay pinned
           * above the card rather than scrolling with it.
           */}
          <div className="flex w-full min-w-0 flex-col">
            <ImpersonatorNotice />
            <SyncingNotice />
          </div>
          {/*
           * The pane card: one rounded surface on the app ground holding every
           * routed pane. It wraps the scroller rather than living inside it so
           * its edges stay pinned while the pane content scrolls within —
           * `overflow-clip` rounds the scroller's corners, `min-h-0` + `flex-1`
           * bound it to the column.
           */}
          <PaneCard>
            {/*
             * The single scroll context for the route (app.css keeps html/body
             * locked). It fills the card, so its vertical scrollbar sits at the
             * card's right edge and pane content spans the card's full width
             * (Figma "Context: Fill Space" — panes carry their own 20px
             * insets). The load-bearing scroller classes (`relative`,
             * `[overflow-anchor:none]`) are documented on
             * `PANE_SCROLLER_CLASSNAME`.
             */}
            <div ref={scrollerRef} onScroll={onScroll} className={PANE_SCROLLER_CLASSNAME}>
              <div className="flex h-full w-full min-w-0 flex-col">
                <Outlet />
              </div>
            </div>
          </PaneCard>
        </div>
      </div>
    </>
  );
};

export default HomeScene;
